"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { FactorStepList } from "@/components/tools/algebra/factor-steps";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  expandFactorization,
  type FactorResult,
  factorPolynomial,
} from "@/lib/tool-kit/algebra/factor";
import {
  formatFactorization,
  type TextOptions,
} from "@/lib/tool-kit/algebra/factor-text";
import { parseSides, singleVariable } from "@/lib/tool-kit/algebra/statement";
import { formatUni, type Uni, uniDegree } from "@/lib/tool-kit/algebra/uni";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import type { NumberStyle } from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const FACTORING_TRINOMIALS_CALCULATOR_STRINGS = {
  polynomial: "Polynomial to factor",
  polynomialHint:
    "One letter, for example 6x^2 + x − 12, x^3 + 8 or 2x^3 − 3x^2 − 11x + 6. Powers use ^ or superscripts.",
  examplesLabel: "Try an example",
  exampleLabels: [
    "Trinomial",
    "GCF",
    "Squares",
    "Cubes",
    "Grouping",
    "Cubic",
    "Quartic",
    "Prime",
  ],
  examples: [
    "6x^2 + x − 12",
    "12x^3 − 27x",
    "9x^2 − 25",
    "27x^3 − 1",
    "x^3 + 2x^2 + 3x + 6",
    "2x^3 − 3x^2 − 11x + 6",
    "x^4 + 4",
    "x^2 + 4",
  ],
  results: "Factored form",
  factored: "Factorization",
  status: "Status",
  primeYes: "Prime over the integers",
  primeNo: "Factored completely",
  copy: "Copy factored form",
  copied: "Copied!",
  stepsHeading: "Step by step",
  checkHeading: "Check by expanding",
  checkVerified:
    "Multiplying the factors back out gives {expanded}, the polynomial you entered.",
  incomplete:
    "A factor of degree 4 or more was left as it is: no pattern, rational root or pair of quadratics split it. It may still factor in a way this tool does not search for.",
  gcf: "Take out the greatest common factor, {gcf}.",
  gcfNegative:
    "Take out the greatest common factor, {gcf}. The leading coefficient is negative, so the factor includes the minus sign.",
  differenceSquares:
    "A difference of squares: A² − B² = (A − B)(A + B), here with A = {a} and B = {b}.",
  perfectSquare: "A perfect square trinomial: A² ± 2AB + B² = (A ± B)².",
  sumCubes:
    "A sum of cubes: A³ + B³ = (A + B)(A² − AB + B²), here with A = {a} and B = {b}.",
  differenceCubes:
    "A difference of cubes: A³ − B³ = (A − B)(A² + AB + B²), here with A = {a} and B = {b}.",
  simpleTrinomial:
    "Find two numbers that multiply to c = {c} and add to b = {b}: they are {m} and {n}.",
  acPair:
    "AC method: multiply a × c = {a} × {c} = {product}. Find two numbers that multiply to {product} and add to b = {b}: they are {m} and {n}.",
  acSplit: "Split the middle term using those two numbers.",
  acGroup:
    "Group the terms in pairs and take the common factor out of each pair.",
  acFactor: "Both pairs now share a bracket; factor it out.",
  groupPairs:
    "Group the four terms in two pairs and take the greatest common factor out of each pair.",
  groupCommon: "Both pairs contain {binomial}, so factor it out.",
  rationalRoot:
    "By the rational root theorem, try fractions p/q where p divides the constant term and q divides the leading coefficient. At {variable} = {root} the polynomial is zero, so it has a matching factor.",
  divideOut:
    "Divide it out: {factor} is a factor, and the quotient is what remains.",
  quadraticPair:
    "Write it as a product of two quadratics with whole-number coefficients.",
  primeQuadratic:
    "This quadratic is prime over the integers: its discriminant b² − 4ac = {discriminant} is not a perfect square, so it has no rational roots.",
  primeQuadraticNegative:
    "This quadratic is prime over the integers: its discriminant b² − 4ac = {discriminant} is negative, so it has no real roots at all.",
  primeCubic:
    "This cubic has no rational root, so it has no linear factor and is prime over the integers.",
  noSplit:
    "No pattern, rational root or pair of quadratics splits this degree-{degree} factor.",
  zeroNote: "Zero has no factorization. Enter a non-zero polynomial.",
  enter: "Enter a polynomial to factor it.",
  errors: {
    empty: "Enter a polynomial to factor it.",
    "too-long": "That expression is too long. Shorten it and try again.",
    character:
      "The character “{token}” is not allowed. Use digits, one letter, + − × / ^ and brackets.",
    unexpected: "Something is out of place near “{token}”.",
    end: "The expression ends too soon. Check for a trailing operator.",
    bracket: "The brackets do not match.",
    "divide-by-variable":
      "Dividing by the variable does not give a polynomial. Divide only by numbers.",
    "divide-by-zero": "Division by zero is not allowed.",
    exponent: "Powers must be whole numbers, such as x^2.",
    "too-large": "That expression is too large to expand (degree above 40).",
    "no-relation": "Enter an expression, not an equation.",
    "many-relations": "Enter an expression, not an equation.",
    "many-variables":
      "More than one letter appears ({token}). This tool factors one-variable polynomials.",
    "missing-side": "Enter a polynomial.",
  },
} as const;

type Strings = typeof FACTORING_TRINOMIALS_CALCULATOR_STRINGS;

const DECIMAL_INPUT = /[.,]/;

function Results({ text, strings }: { text: string; strings: Strings }) {
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const parsed = parseSides([text]);
  if (!parsed.ok) {
    const template =
      strings.errors[parsed.error.code as keyof Strings["errors"]] ??
      strings.errors.empty;
    return (
      <ErrorNotice>{t(template, { token: parsed.error.token })}</ErrorNotice>
    );
  }
  const read = singleVariable(parsed.polys);
  if (!read.ok) {
    return (
      <ErrorNotice>
        {t(strings.errors["many-variables"], { token: read.error.token })}
      </ErrorNotice>
    );
  }
  const p: Uni = read.parts[0];
  const style: NumberStyle = DECIMAL_INPUT.test(text) ? "decimal" : "fraction";
  const options: TextOptions = { variable: read.variable, style, mark };
  const result = factorPolynomial(p);
  if (result === null) {
    return <Notice>{strings.zeroNote}</Notice>;
  }
  return <Outcome options={options} p={p} result={result} strings={strings} />;
}

function Outcome({
  result,
  p,
  options,
  strings,
}: {
  result: FactorResult;
  p: Uni;
  options: TextOptions;
  strings: Strings;
}) {
  const factored = formatFactorization(result, options);
  const expanded = formatUni(
    expandFactorization(result),
    options.variable,
    options.style,
    options.mark
  );
  const prime =
    uniDegree(p) >= 2 &&
    result.factors.length === 1 &&
    result.factors[0].exponent === 1 &&
    result.xPower === 0 &&
    result.constant.num === result.constant.den;
  const ctx = { ...options, strings };
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.factored} value={factored} />
        <StatCard
          label={strings.status}
          value={prime ? strings.primeYes : strings.primeNo}
        />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={factored}
      />
      {result.steps.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
          <FactorStepList ctx={ctx} result={result} />
        </>
      ) : null}
      {result.complete ? null : <Notice>{strings.incomplete}</Notice>}
      {result.verified ? (
        <>
          <h3 className={WIDGET_H3}>{strings.checkHeading}</h3>
          <p className="text-muted-foreground text-sm">
            {t(strings.checkVerified, { expanded })}
          </p>
        </>
      ) : null}
    </>
  );
}

export default function FactoringTrinomialsCalculatorWidget() {
  const strings = useToolUi(FACTORING_TRINOMIALS_CALCULATOR_STRINGS);
  const [text, setText] = useState<string>(strings.examples[0]);
  return (
    <>
      <TextField
        hint={strings.polynomialHint}
        id="fac-input"
        label={strings.polynomial}
        mono
        onChange={setText}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-sm">
          {strings.examplesLabel}
        </span>
        {strings.examples.map((example, i) => (
          <button
            className={BUTTON_SMALL}
            key={example}
            onClick={() => setText(example)}
            type="button"
          >
            {strings.exampleLabels[i]}
          </button>
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {text.trim() === "" ? (
          <Notice>{strings.enter}</Notice>
        ) : (
          <Results strings={strings} text={text} />
        )}
      </section>
    </>
  );
}
